/**
 * DATUM, VECKOR OCH TAL — de omräkningar varje skärm behöver.
 *
 * DE LIGGER HÄR AV ETT ENDA SKÄL: annars skriver fyra agenter fyra
 * veckoberäkningar, och tre av dem är fel i vecka 53.
 *
 * ==========================================================================
 * BESLUT 04: VECKAN ÄR ENDA PERIODBEGREPPET I TIDRAPPORTERINGEN.
 * Attest per vecka, lås per vecka, ISO 'YYYY-Www'. Månaden finns inte —
 * shared/types.ts säger det rakt ut: "INGEN KALENDERMÅNADSTYP FINNS".
 * Månadsrutnäten i prototypen är NAVIGERING (ett sätt att bläddra till en dag)
 * och bär aldrig ett tillstånd.
 *
 * ISO-VECKOÅRET ÄR INTE KALENDERÅRET. 'YYYY' i 'YYYY-Www' är veckoåret: den
 * 1 januari 2027 ligger i 2026-W53. Räkna aldrig ut veckan med
 * `datum.getFullYear()` — det ger fel år sex dagar om året, och de sex dagarna
 * hamnar i fel attestvecka.
 *
 * ARBETSDAGEN ÄR EUROPE/STOCKHOLM. schema/company.sql: work_date är TEXT
 * 'YYYY-MM-DD' i Europe/Stockholm. En montör som sparar 23:30 den 3:e ska få
 * den 3:e, inte den 4:e som UTC skulle ge honom halva året. `idag()` läser
 * därför klockan genom Intl med tidszonen satt, aldrig genom
 * `new Date().toISOString()`.
 *
 * VECKORÄKNINGEN GÖRS I UTC. En arbetsdag är ett KALENDERDATUM utan klockslag,
 * så aritmetiken ska inte röra sommartid. Räkna i UTC och sommartidsskiftet
 * blir aldrig en dag som försvinner eller kommer två gånger.
 * ==========================================================================
 */

import { asIsoWeek, asWorkDate } from '@delat/types';
import type { IsoWeek, Meters, Millis, Minutes, Ore, WorkDate } from '@delat/types';

const DYGN_MS = 86_400_000;
const TIDSZON = 'Europe/Stockholm';

/* == Arbetsdagen ========================================================= */

/** Dagens arbetsdag i Europe/Stockholm. */
export const idag = (nu: number = Date.now()): WorkDate => tillArbetsdag(nu);

/** En tidsstämpel (Millis) -> arbetsdagen den hör till, i Europe/Stockholm. */
export const tillArbetsdag = (tid: number | Millis): WorkDate => {
  // 'en-CA' ger ISO-formen 'YYYY-MM-DD' direkt. Delarna hämtas i stället för
  // att formatera fram en sträng som sedan måste tolkas tillbaka.
  const delar = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIDSZON,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(Number(tid)));
  return asWorkDate(delar);
};

const utcFor = (dag: WorkDate): number => Date.parse(`${dag}T00:00:00Z`);

const franUtc = (ms: number): WorkDate => asWorkDate(new Date(ms).toISOString().slice(0, 10));

/** Dagar framåt (negativt bakåt). Sommartid kan inte störa — räkningen är i UTC. */
export const plusDagar = (dag: WorkDate, dagar: number): WorkDate =>
  franUtc(utcFor(dag) + dagar * DYGN_MS);

/** Hela dagar mellan två arbetsdagar. */
export const dagarMellan = (fran: WorkDate, till: WorkDate): number =>
  Math.round((utcFor(till) - utcFor(fran)) / DYGN_MS);

/** Måndag = 0 ... söndag = 6. ISO:s ordning, inte JavaScripts. */
export const veckodag = (dag: WorkDate): number => (new Date(utcFor(dag)).getUTCDay() + 6) % 7;

export const arHelg = (dag: WorkDate): boolean => veckodag(dag) >= 5;

/* == ISO-veckan ========================================================== */

/** Arbetsdag -> 'YYYY-Www'. Torsdagsregeln, alltså ISO 8601. */
export const veckaFor = (dag: WorkDate): IsoWeek => {
  // Torsdagen i dagens vecka bestämmer både veckoåret och veckonumret.
  const torsdag = new Date(utcFor(dag) + (3 - veckodag(dag)) * DYGN_MS);
  const veckoar = torsdag.getUTCFullYear();
  // Den 4 januari ligger alltid i vecka 1; torsdagen i DEN veckan är ankaret.
  const jan4 = new Date(Date.UTC(veckoar, 0, 4));
  const forstaTorsdag = new Date(
    jan4.getTime() + (3 - ((jan4.getUTCDay() + 6) % 7)) * DYGN_MS,
  );
  const nummer = 1 + Math.round((torsdag.getTime() - forstaTorsdag.getTime()) / (7 * DYGN_MS));
  return asIsoWeek(`${veckoar}-W${String(nummer).padStart(2, '0')}`);
};

/** 'YYYY-Www' -> veckans måndag. */
export const mandagen = (vecka: IsoWeek): WorkDate => {
  const veckoar = Number(vecka.slice(0, 4));
  const nummer = Number(vecka.slice(6));
  const jan4 = Date.UTC(veckoar, 0, 4);
  const jan4Dag = (new Date(jan4).getUTCDay() + 6) % 7;
  return franUtc(jan4 - jan4Dag * DYGN_MS + (nummer - 1) * 7 * DYGN_MS);
};

/** Veckans sju arbetsdagar, måndag först. Det är kalenderremsans innehåll. */
export const dagarIVecka = (vecka: IsoWeek): readonly WorkDate[] => {
  const start = mandagen(vecka);
  return [0, 1, 2, 3, 4, 5, 6].map((n) => plusDagar(start, n));
};

/**
 * Veckor framåt eller bakåt. Räkningen går via måndagen och inte via
 * veckonumret — 2026-W53 + 1 är 2027-W01, inte 2026-W54.
 */
export const plusVeckor = (vecka: IsoWeek, veckor: number): IsoWeek =>
  veckaFor(plusDagar(mandagen(vecka), veckor * 7));

export const veckanNu = (nu: number = Date.now()): IsoWeek => veckaFor(idag(nu));

/* == Text till människor ================================================= */

const VECKODAGAR_KORT = ['M', 'T', 'O', 'T', 'F', 'L', 'S'] as const;
const VECKODAGAR = [
  'måndag',
  'tisdag',
  'onsdag',
  'torsdag',
  'fredag',
  'lördag',
  'söndag',
] as const;

/** 'M' ... 'S'. Kalenderremsans lilla bokstav över dagen. */
export const veckodagKort = (dag: WorkDate): string =>
  VECKODAGAR_KORT[veckodag(dag)] ?? '';

/** "Torsdag 3 september". Datumradens starka del. */
export const visaDag = (dag: WorkDate): string => {
  const namn = VECKODAGAR[veckodag(dag)] ?? '';
  const datum = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
  }).format(new Date(utcFor(dag)));
  return `${namn.charAt(0).toUpperCase()}${namn.slice(1)} ${datum}`;
};

/** "Vecka 36". */
export const visaVecka = (vecka: IsoWeek): string => `Vecka ${Number(vecka.slice(6))}`;

/**
 * "Torsdag 27 augusti 2026". visaDag utelämnar året med flit — för dagens vecka
 * är det brus — men ett ankardatum eller ett registreringsdatum kan ligga i ett
 * annat år, och då är året hela poängen.
 */
export const visaDagMedAr = (dag: WorkDate): string => `${visaDag(dag)} ${dag.slice(0, 4)}`;

const svKortDatum = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
});

/**
 * 1758924000000 -> "27 sep.". Kort form för en TIDSSTÄMPEL, för de ställen där
 * två datum ska rymmas på samma rad. Går via tillArbetsdag så att dagen blir
 * densamma som resten av appen räknar med: Europe/Stockholm, aldrig UTC.
 */
export const visaKortDatum = (tid: Millis | number): string =>
  svKortDatum.format(new Date(`${tillArbetsdag(tid)}T00:00:00Z`));

/** "27 aug. – 27 sep. 2026". Året står en gång, på slutet. */
export const visaPeriod = (fran: Millis | number, till: Millis | number): string =>
  `${visaKortDatum(fran)} – ${visaKortDatum(till)} ${tillArbetsdag(till).slice(0, 4)}`;

/* == Tal till människor ==================================================
   ALLA BELOPP I KONTRAKTET ÄR EX MOMS OCH FÖRE ROT (shared/types.ts, första
   stycket). Formateringen nedan sätter därför ALDRIG ut "ink. moms", och den
   som visar ett belopp för en PRIVATKUND måste själv skriva ut vilket det är.
   ======================================================================== */

const svTal = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });
const svTal1 = new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** 450 -> "7,5 h". Minuter är kontraktets enhet för arbetstid. */
export const visaMinuter = (minuter: Minutes | number): string =>
  `${svTal1.format(Number(minuter) / 60)} h`;

/** 129500 -> "1 295 kr". Ören är kontraktets enhet för belopp. Ex moms. */
export const visaOren = (oren: Ore | number): string => {
  const kronor = Number(oren) / 100;
  const heltKrontal = Number.isInteger(kronor);
  return heltKrontal
    ? `${svTal.format(kronor)} kr`
    : `${new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(kronor)} kr`;
};

/** 45000 -> "4,5 mil". Meter är kontraktets enhet för sträcka. */
export const visaMil = (meter: Meters | number): string =>
  `${svTal1.format(Number(meter) / 10000)} mil`;

/** 45000 -> "4,5 km" när milen inte är rätt enhet. */
export const visaKm = (meter: Meters | number): string =>
  `${svTal1.format(Number(meter) / 1000)} km`;

/** 2500 -> "2,5". Materialantal ligger i tusendelar. */
export const visaTusendel = (tusendelar: number): string =>
  svTal1.format(tusendelar / 1000);
